import React, { useState } from 'react';
import { T } from '../shared/constants';
import { ItemFrame, Instruction, CheckButton, ResultCard } from './ItemChrome';
import { buildStudentAnswer, isCorrectResponse, feedbackFor, partMarks } from './naplanItems';

function splitTemplate(template) {
  const parts = [];
  const re = /\{(\d+)\}/g;
  let last = 0;
  let m;
  const t = String(template || '');
  while ((m = re.exec(t))) {
    if (m.index > last) parts.push({ text: t.slice(last, m.index) });
    parts.push({ gap: Number(m[1]) });
    last = re.lastIndex;
  }
  if (last < t.length) parts.push({ text: t.slice(last) });
  return parts;
}

/** `inline` — choose from a drop-down inside a sentence. Response: chosen option index per gap. */
export default function InlineRenderer({ question, studentAnswer, state, showExplanation, onSubmit, onAskLumi, onExplainVideo }) {
  const isAnswered = studentAnswer != null;
  const blanks = question.blanks || [];
  const [draft, setDraft] = useState(() => blanks.map(() => null));
  const picks = isAnswered && Array.isArray(studentAnswer.rawAnswer) ? studentAnswer.rawAnswer : draft;
  const isCorrect = isAnswered ? (typeof studentAnswer.isCorrect === 'boolean' ? studentAnswer.isCorrect : isCorrectResponse(question, picks)) : false;
  const marks = isAnswered ? partMarks(question, picks) : [];
  const complete = blanks.length > 0 && draft.every(p => p !== null && p !== undefined);

  function choose(g, v) {
    setDraft(d => { const n = [...d]; n[g] = v === '' ? null : Number(v); return n; });
  }

  function submit() {
    if (isAnswered || !complete) return;
    onSubmit(buildStudentAnswer(question, [...draft]));
  }

  return (
    <ItemFrame question={question} state={state} isAnswered={isAnswered}>
      <Instruction>Choose the best option for each gap</Instruction>
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "16px 18px", marginBottom: 20, fontSize: 16, lineHeight: 2.4, color: T.text, whiteSpace: "pre-wrap" }}>
        {splitTemplate(question.template).map((p, k) => {
          if (p.text !== undefined) return <React.Fragment key={k}>{p.text}</React.Fragment>;
          const g = p.gap;
          const b = blanks[g];
          if (!b) return <React.Fragment key={k}>___</React.Fragment>;
          const v = picks[g];
          const border = isAnswered ? (marks[g] ? T.green : T.red) : v !== null && v !== undefined ? T.accent : T.borderHi;
          const bg = isAnswered ? (marks[g] ? T.greenLo : T.redLo) : T.surface;
          return (
            <span key={k} style={{ whiteSpace: "nowrap" }}>
              <select
                aria-label={`Gap ${g + 1}`}
                value={v === null || v === undefined ? '' : String(v)}
                disabled={isAnswered}
                data-status={isAnswered ? (marks[g] ? 'right' : 'wrong') : 'none'}
                onChange={e => choose(g, e.target.value)}
                style={{
                  font: "inherit", fontSize: 15, fontWeight: 600, padding: "4px 8px", margin: "0 2px",
                  borderRadius: 8, border: `1.5px solid ${border}`, background: bg,
                  color: isAnswered ? (marks[g] ? T.green : T.red) : T.text, maxWidth: "100%",
                }}
              >
                <option value="">Choose…</option>
                {(b.options || []).map((o, oi) => <option key={oi} value={String(oi)}>{o}</option>)}
              </select>
              {isAnswered && <span aria-hidden="true" style={{ fontWeight: 800, color: marks[g] ? T.green : T.red, marginRight: 2 }}>{marks[g] ? '✓' : '✗'}</span>}
            </span>
          );
        })}
      </div>
      {!isAnswered && <CheckButton mode={state.mode} disabled={!complete} onClick={submit} />}
      <ResultCard
        question={question} mode={state.mode} isAnswered={isAnswered} isCorrect={isCorrect}
        showExplanation={showExplanation} feedback={isAnswered && !isCorrect ? feedbackFor(question, picks) : null}
        onAskLumi={onAskLumi} onExplainVideo={onExplainVideo}
      />
    </ItemFrame>
  );
}
